import { Controller } from "@hotwired/stimulus"
import { DocumentUploader } from "../services/document_uploader"
import { FileValidator } from "../services/file_validator"
import { UploadQueue } from "../services/upload_queue"

export default class extends Controller {
  static targets = ["input", "button", "dropZone", "selectButton"]
  static values = {
    batchSize: { type: Number, default: 3 },
    allowedTypes: { type: String, default: "image/jpeg,image/png,image/tiff,image/gif,image/webp,application/pdf,text/plain,text/markdown,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
    maxSize: Number,
    maxQueueSize: { type: Number, default: 50 }
  }

  #uploader = null
  #validator = null
  #uploadQueue = null
  #processing = false
  #boundPreventDefaults = null
  #boundHandleDragEnter = null
  #boundHandleDragLeave = null
  #boundHandleDrop = null
  #dragDropInitialized = false

  connect() {
    if (this.hasDropZoneTarget) {
      this.#boundPreventDefaults = this.#preventDefaults.bind(this)
      this.#boundHandleDragEnter = this.#handleDragEnter.bind(this)
      this.#boundHandleDragLeave = this.#handleDragLeave.bind(this)
      this.#boundHandleDrop = this.#handleDrop.bind(this)
      this.#setupDragAndDrop()
    }
  }

  disconnect() {
    this.#processing = false

    if (this.#dragDropInitialized) {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        document.body.removeEventListener(eventName, this.#boundPreventDefaults)
      })

      if (this.hasDropZoneTarget) {
        ['dragenter', 'dragover'].forEach(eventName => {
          this.dropZoneTarget.removeEventListener(eventName, this.#boundHandleDragEnter)
        })

        this.dropZoneTarget.removeEventListener('dragleave', this.#boundHandleDragLeave)
        this.dropZoneTarget.removeEventListener('drop', this.#boundHandleDrop)
      }
    }

    if (this.#uploadQueue) {
      this.#uploadQueue.reset()
      this.#uploadQueue = null
    }

    this.#uploader = null
    this.#validator = null
    this.#boundPreventDefaults = null
    this.#boundHandleDragEnter = null
    this.#boundHandleDragLeave = null
    this.#boundHandleDrop = null
  }

  openFileSelector(event) {
    const button = event.currentTarget
    button.disabled = true
    button.classList.add('opacity-60', 'cursor-wait')

    const cleanup = () => {
      button.disabled = false
      button.classList.remove('opacity-60', 'cursor-wait')
      window.removeEventListener('focus', cleanup)
    }

    window.addEventListener('focus', cleanup, { once: true })

    this.inputTarget.click()
  }

  handleFiles() {
    const buttons = this.element.querySelectorAll('button')
    buttons.forEach(btn => {
      btn.disabled = false
      btn.classList.remove('opacity-60', 'cursor-wait')
    })

    const files = Array.from(this.inputTarget.files)
    this.#processFileSelection(files)
    this.inputTarget.value = ''
  }

  #processFileSelection(files) {
    if (files.length === 0) return

    this.#ensureInitialized()

    if (files.length > this.maxQueueSizeValue) {
      this.#dispatchError('Selection', `Please select no more than ${this.maxQueueSizeValue} files at once`)
      return
    }

    this.#uploadQueue.addFiles(files)
    this.#dispatchProgress()
    this.#processUploads()
  }

  dismiss() {
    this.#processing = false
    this.#uploadQueue.reset()
  }

  #ensureInitialized() {
    if (this.#uploader) return
    this.#initializeServices()
  }

  #initializeServices() {
    try {
      const form = this.inputTarget.closest("form")

      if (!form) {
        throw new Error("Missing required form")
      }

      this.#uploader = new DocumentUploader({
        directUploadUrl: this.inputTarget.dataset.directUploadUrl,
        formAction: form.action,
        form: form
      })

      this.#validator = new FileValidator({
        allowedTypes: this.allowedTypesValue,
        maxSize: this.maxSizeValue
      })

      this.#uploadQueue = new UploadQueue()

    } catch (error) {
      console.error('DocumentUploadTwController initialization error:', error)
      this.#dispatchError('System', 'Failed to initialize document uploader')
    }
  }

  #setupDragAndDrop() {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      document.body.addEventListener(eventName, this.#boundPreventDefaults, { passive: false })
    });

    ['dragenter', 'dragover'].forEach(eventName => {
      this.dropZoneTarget.addEventListener(eventName, this.#boundHandleDragEnter, { passive: false })
    });

    this.dropZoneTarget.addEventListener('dragleave', this.#boundHandleDragLeave, { passive: true })
    this.dropZoneTarget.addEventListener('drop', this.#boundHandleDrop, { passive: false })

    this.#dragDropInitialized = true
  }

  #preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  #handleDragEnter(e) {
    this.dropZoneTarget.dataset.dropTarget = ''
  }

  #handleDragLeave(e) {
    delete this.dropZoneTarget.dataset.dropTarget
  }

  #handleDrop(e) {
    delete this.dropZoneTarget.dataset.dropTarget

    if (!e.dataTransfer.types.includes('Files')) {
      return
    }

    const files = Array.from(e.dataTransfer.files)
    this.#processFileSelection(files)
  }

  async #processUploads() {
    if (this.#processing) return
    this.#processing = true

    const activeUploads = []

    while (!this.#uploadQueue.isEmpty() || activeUploads.length > 0) {
      while (this.#uploadQueue.canProcessMore(this.batchSizeValue) && activeUploads.length < this.batchSizeValue) {
        const item = this.#uploadQueue.getNext()
        if (!item) break

        const { file } = item

        const uploadPromise = this.#processFile(file)
          .catch(error => {
            console.error('Upload error:', error)
            this.#dispatchError(file.name, error.message || 'Upload failed')
          })
          .finally(() => {
            this.#uploadQueue.recordCompletion()
            this.#dispatchProgress()
            activeUploads.splice(activeUploads.indexOf(uploadPromise), 1)
          })

        activeUploads.push(uploadPromise)
      }

      if (activeUploads.length > 0) {
        await Promise.race(activeUploads)
      }
    }

    this.#processing = false
    this.#checkUploadComplete()
  }


  async #processFile(file) {
    const validationError = this.#validator.validate(file)
    if (validationError) {
      this.#dispatchError(file.name, validationError)
      return
    }

    const { error: uploadError, signedId } = await this.#uploader.uploadFile(file)
    if (uploadError) {
      this.#dispatchError(file.name, uploadError)
      return
    }

    const { error: createError, html } = await this.#uploader.createDocument(signedId)
    if (createError) {
      this.#dispatchError(file.name, createError)
      return
    }

    if (html?.trim()) {
      Turbo.renderStreamMessage(html)
    }
  }

  #checkUploadComplete() {
    this.#dispatchProgress()
    this.dispatch("complete")
    this.#uploadQueue.reset()
  }

  #dispatchProgress() {
    const progress = this.#uploadQueue.getProgress()
    this.dispatch("progress", { detail: { processed: progress.completed, total: progress.total } })
  }

  #dispatchError(filename, message) {
    this.dispatch("error", { detail: { filename, message } })
  }
}
